/**
 * Loop prevention for the Sphere AI agent loop (MDOT-24553).
 *
 * The agent loop runs in sphere-mcp, whose own step guard stops a spinning run and stamps the
 * verdict on the finished message (`metadata.custom.loopStop`). {@link serverLoopStop} reads that
 * verdict back, and the chat offers Continue (a "Continue" user message with a one-shot advisory)
 * or Stop — see SphereAILoopNotice. {@link detectRunawayLoop} is the older browser-side detection over one run's
 * parts; the chat no longer calls it. `ponytail:` delete it with its tests when convenient.
 *
 * Kept as a pure function over the AI SDK `UIMessage[]` so the real logic is unit-testable in
 * isolation (cf. lockedUserMessages.ts).
 */
import { type UIMessage, getToolName, isToolUIPart } from "ai";

/**
 * Model turns (steps) one run may take before the guard parks it and asks the user whether to
 * continue. Generous on purpose: a legitimate request can chain a fair number of MCP calls
 * (inspect portfolio → list instruments → run something → read the result), and every parked
 * step the user answers grants a fresh budget anyway — so this only has to be low enough to
 * bound a genuinely runaway loop.
 */
export const MAX_TURNS_PER_RUN = 16;

/**
 * How many *identical* tool calls (same name, same arguments) or identical assistant replies
 * within one run count as a loop. Two identical calls can be a legitimate retry; three is the
 * model going in circles.
 */
export const IDENTICAL_REPEAT_LIMIT = 3;

/**
 * A minimal structural view of the pieces of a `UIMessage` this detection reads. Kept loose so
 * tests can build fixtures without constructing full AI SDK messages, while the production call
 * passes real `UIMessage[]`.
 */
export type LoopScanMessage = {
	id: string;
	role: UIMessage["role"];
	parts: ReadonlyArray<unknown>;
	/**
	 * Only `custom.loopStop` is read, and only by {@link serverLoopStop} — the rest of
	 * this module judges a run from its PARTS.
	 *
	 * `unknown`, and narrowed at the one place that reads it, because the AI SDK types
	 * `UIMessage.metadata` as `unknown` too: declaring a shape here would make every
	 * real `UIMessage[]` unassignable to this type, which is the opposite of what
	 * "kept loose so tests can build fixtures" is for. It is also honest — the value
	 * crosses a network boundary, so a shape asserted here would be a claim about
	 * another repository's output rather than a fact.
	 */
	metadata?: unknown;
};

/**
 * Where in the current run the guard starts counting. `runId` is the trailing assistant
 * message's id, so a baseline granted for an earlier run is ignored once the user speaks again
 * (which starts a new assistant message). `null` = count the whole run.
 */
export type LoopGuardBaseline = { runId: string; fromPartIndex: number } | null;

export type LoopStopReason = "max-turns" | "repeated-tool-call" | "repeated-message";

export type LoopStop = {
	reason: LoopStopReason;
	/** Id of the assistant message the runaway run lives in — identifies the trip in telemetry. */
	runId: string;
	/** Model turns counted in the window that tripped the guard. */
	turns: number;
	/** User-facing sentence for the "keep going?" prompt. */
	summary: string;
	/**
	 * Hidden guidance handed to the model on the turn the user resumes with, so "continue"
	 * doesn't just replay the step that tripped the guard.
	 */
	advisory: string;
};

/**
 * The user-facing sentence and the model-facing advisory for a repeated tool call.
 *
 * One function because two detections now produce this verdict — the local
 * {@link detectRunawayLoop} and {@link serverLoopStop}, which reads sphere-mcp's own —
 * and the user must read the same thing whichever caught the run. A second copy of
 * these strings is a second wording waiting to disagree.
 */
function repeatedToolCallText(label: string, count: number): { summary: string; advisory: string } {
	return {
		summary: `Sphere AI has called "${label}" ${count} times with the same input without getting anywhere.`,
		advisory:
			`Loop guard: your previous attempt called "${label}" ${count} times with identical ` +
			"arguments and made no progress, so it was interrupted and the user asked you to continue. Do NOT call that " +
			"tool again with the same arguments. Either take a different approach (a different tool, or different " +
			"arguments) or tell the user plainly what is blocking you and what you need from them.",
	};
}

/** The run in progress: the last message, but only while it's the assistant's. */
function trailingAssistantMessage(messages: ReadonlyArray<LoopScanMessage>): LoopScanMessage | null {
	const last = messages[messages.length - 1];
	return last?.role === "assistant" ? last : null;
}

/**
 * A baseline pinned at the END of the current run — i.e. "everything so far is forgiven, start
 * counting from here". Returns `null` when there is no run in progress (nothing to forgive).
 */
export function currentRunBaseline(messages: ReadonlyArray<LoopScanMessage>): LoopGuardBaseline {
	const message = trailingAssistantMessage(messages);
	return message ? { runId: message.id, fromPartIndex: message.parts.length } : null;
}

/**
 * Model turns in a slice of a run. One `step-start` part is pushed per model turn: the
 * server emits exactly one `start-step` chunk per request, because no tool has a
 * server-side `execute` and so every request is a single step (asserted in
 * sphere-mcp/src/server/chat/stream.test.ts). Counting them counts turns.
 */
export function countTurns(parts: ReadonlyArray<unknown>): number {
	let turns = 0;
	for (const part of parts) {
		if ((part as { type?: unknown }).type === "step-start") turns++;
	}
	return turns;
}

/**
 * Order-independent serialization of a tool call's arguments, so `{a:1,b:2}` and `{b:2,a:1}`
 * compare equal — the model re-emits the same call with no guarantee of key order. Inputs come
 * from `JSON.parse`, so there are no cycles to worry about.
 */
function stableKey(value: unknown): string {
	if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "undefined";
	if (Array.isArray(value)) return `[${value.map(stableKey).join(",")}]`;
	const entries = Object.entries(value as Record<string, unknown>)
		.filter(([, v]) => v !== undefined)
		.sort(([a], [b]) => (a < b ? -1 : 1));
	return `{${entries.map(([key, v]) => `${JSON.stringify(key)}:${stableKey(v)}`).join(",")}}`;
}

/** An occurrence to be counted: `key` decides identity, `label` is what we tell the user. */
type RepeatEntry = { key: string; label: string };

type RepeatHit = { label: string; count: number };

/**
 * The first key to reach `limit` occurrences, scanning in transcript order. Occurrences need
 * NOT be consecutive: an A→B→A→B→A ping-pong is as much a loop as A→A→A, and both are caught.
 */
function findRepeat(entries: ReadonlyArray<RepeatEntry>, limit: number): RepeatHit | null {
	const hits = new Map<string, RepeatHit>();
	for (const entry of entries) {
		const hit = hits.get(entry.key);
		if (!hit) {
			hits.set(entry.key, { label: entry.label, count: 1 });
			continue;
		}
		hit.count++;
		if (hit.count >= limit) return hit;
	}
	return null;
}

/** Every tool call in the slice, keyed by tool name + arguments. */
function toolCallEntries(parts: ReadonlyArray<unknown>): RepeatEntry[] {
	const entries: RepeatEntry[] = [];
	for (const part of parts) {
		if (!isToolUIPart(part as never)) continue;
		const name = getToolName(part as never);
		entries.push({ key: `${name}\u0000${stableKey((part as { input?: unknown }).input)}`, label: name });
	}
	return entries;
}

/** Non-empty text of a text part, whitespace-collapsed and case-folded; `null` for anything else. */
function normalizedReplyText(part: unknown): string | null {
	const candidate = part as { type?: unknown; text?: unknown };
	if (candidate.type !== "text" || typeof candidate.text !== "string") return null;
	const normalized = candidate.text.trim().replace(/\s+/g, " ").toLowerCase();
	return normalized.length > 0 ? normalized : null;
}

/**
 * What the assistant actually SAID in each step of the slice: the text emitted after that step's
 * last tool call (or all of its text, when the step made no calls).
 *
 * Text that PRECEDES a tool call is interim narration — "let me check…", status updates, the
 * very parts the thread folds away into its Details disclosure (see narration.ts) — and a model
 * that reuses the same filler line across a legitimate multi-step run must not read as a loop.
 * A model re-sending the same *reply* is the signal we're after. (Filler repeated in front of
 * genuinely different work still gets caught, later, by the turn cap.)
 */
function replyTextEntries(parts: ReadonlyArray<unknown>): RepeatEntry[] {
	const entries: RepeatEntry[] = [];
	let stepReplies: RepeatEntry[] = [];
	for (const part of parts) {
		if ((part as { type?: unknown }).type === "step-start") {
			entries.push(...stepReplies);
			stepReplies = [];
			continue;
		}
		if (isToolUIPart(part as never)) {
			// Everything said so far in this step was narration leading up to the call.
			stepReplies = [];
			continue;
		}
		const normalized = normalizedReplyText(part);
		if (normalized) stepReplies.push({ key: normalized, label: normalized });
	}
	entries.push(...stepReplies);
	return entries;
}

/**
 * Detect a runaway agent loop in the run currently in progress, or `null` when it looks
 * healthy. `baseline` (see {@link LoopGuardBaseline}) excludes the part of the run the user has
 * already blessed; `limits` exists for tests and for tuning at the call site.
 *
 * Only ever inspects the trailing assistant message, so a finished conversation (last message
 * from the user, or no messages at all) is never a loop.
 */
export function detectRunawayLoop(
	messages: ReadonlyArray<LoopScanMessage>,
	baseline: LoopGuardBaseline = null,
	limits: { maxTurns?: number; identicalRepeatLimit?: number } = {},
): LoopStop | null {
	const message = trailingAssistantMessage(messages);
	if (!message) return null;

	const maxTurns = limits.maxTurns ?? MAX_TURNS_PER_RUN;
	const repeatLimit = limits.identicalRepeatLimit ?? IDENTICAL_REPEAT_LIMIT;
	// A baseline granted for a previous run no longer applies — scan the run from its start.
	const from = baseline !== null && baseline.runId === message.id ? baseline.fromPartIndex : 0;
	const parts = message.parts.slice(from);
	const turns = countTurns(parts);

	const repeatedTool = findRepeat(toolCallEntries(parts), repeatLimit);
	if (repeatedTool) {
		return {
			reason: "repeated-tool-call",
			runId: message.id,
			turns,
			...repeatedToolCallText(repeatedTool.label, repeatedTool.count),
		};
	}

	const repeatedMessage = findRepeat(replyTextEntries(parts), repeatLimit);
	if (repeatedMessage) {
		return {
			reason: "repeated-message",
			runId: message.id,
			turns,
			summary: `Sphere AI has repeated the same reply ${repeatedMessage.count} times.`,
			advisory:
				`Loop guard: your previous attempt sent the same reply ${repeatedMessage.count} times, so it was interrupted ` +
				"and the user asked you to continue. Do not send that reply again. Either take a concrete next step towards " +
				"the request or ask the user one specific question.",
		};
	}

	if (turns >= maxTurns) {
		return {
			reason: "max-turns",
			runId: message.id,
			turns,
			summary: `Sphere AI has taken ${turns} steps on this request without finishing.`,
			advisory:
				`Loop guard: you have taken ${turns} steps on this request without finishing, so you were interrupted and ` +
				"the user asked you to continue. Re-read the conversation, then take the SHORTEST path to a final answer. " +
				"If you cannot finish, say what is missing and what you need from the user instead of calling more tools.",
		};
	}

	return null;
}

/**
 * The stop sphere-mcp's OWN guard already made, read off the trailing assistant
 * message — or `null` when the server stopped nothing, which is every ordinary turn.
 *
 * Two guards now watch one conversation, because the agent loop moved server-side
 * (MDOT-25078) and each can see what the other cannot. {@link detectRunawayLoop}
 * judges the run the browser drives, from the parts it holds. sphere-mcp's
 * `step-loop-guard.ts` judges the steps it takes inside ONE request, which the
 * browser never sees as separate turns — it receives them already resolved, in a
 * single assistant message, which is precisely the shape `sendAutomaticallyWhen`
 * treats as "ready to continue". So a server-stopped run that is not read back here
 * gets restarted by this client a moment later, and the server's guard accomplishes
 * nothing.
 *
 * Returns the same {@link LoopStop} shape the local detection does, with the same
 * wording: the server sends the tool name and the count, and the sentences are built
 * here, so the text the user reads has exactly one owner.
 *
 * The server's step ceiling stops a run too: its last answer is already a written
 * partial one, stamped `{ reason: "max-turns", steps }`, and Continue picks up what
 * that answer said was left.
 */
export function serverLoopStop(messages: ReadonlyArray<LoopScanMessage>): LoopStop | null {
	const message = trailingAssistantMessage(messages);
	// This arrived over the wire: every hop is optional, and each field is type-checked below before use.
	const custom = (message?.metadata as { custom?: unknown } | undefined)?.custom;
	const stop = (custom as { loopStop?: unknown } | undefined)?.loopStop as
		| { toolName?: unknown; count?: unknown; reason?: unknown; steps?: unknown }
		| undefined;
	const steps = typeof stop?.steps === "number" && Number.isFinite(stop.steps) ? stop.steps : null;
	if (message && stop?.reason === "max-turns" && steps !== null && steps > 0) {
		return {
			reason: "max-turns",
			runId: message.id,
			turns: steps,
			summary: `Sphere AI used all ${steps} steps it has for one answer, so the answer above is partial.`,
			advisory:
				`Step limit: your previous answer used all ${steps} steps and was cut short, and the user asked you to ` +
				"continue. Do NOT redo what that answer already covered: carry on with what it said was left, then " +
				"give the complete answer.",
		};
	}
	const toolName = typeof stop?.toolName === "string" ? stop.toolName : null;
	const count = typeof stop?.count === "number" && Number.isFinite(stop.count) ? stop.count : null;
	if (!message || toolName === null || count === null || count <= 0) return null;
	return {
		reason: "repeated-tool-call",
		runId: message.id,
		// The server counts identical CALLS, not model turns; this is the closest true
		// number it has, and it is what the summary below quotes.
		turns: count,
		...repeatedToolCallText(toolName, count),
	};
}
